"use client";

import { Link } from "react-router-dom";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Logo, Section } from "@/src/components";
import {
  Palette,
  Type,
  Image,
  MessageSquare,
  Target,
  Heart,
} from "lucide-react";

const brandSections = [
  {
    title: "Logo",
    description: "Logo usage guidelines, clear space, and variations",
    href: "/docs/brand/logo",
    icon: <Image className="icon-lg" />,
  },
  {
    title: "Colors",
    description: "Color palette, semantic colors, and usage guidelines",
    href: "/docs/tokens/colors",
    icon: <Palette className="icon-lg" />,
  },
  {
    title: "Typography",
    description: "Type scale, font families, and text styles",
    href: "/docs/tokens/typography",
    icon: <Type className="icon-lg" />,
  },
  {
    title: "Voice & Tone",
    description: "How we communicate with users",
    href: "/docs/brand/voice",
    icon: <MessageSquare className="icon-lg" />,
  },
];

const brandValues = [
  {
    icon: <Target className="icon-md" />,
    title: "Clarity",
    description: "Every element serves a purpose. We remove complexity, not hide it.",
  },
  {
    icon: <Heart className="icon-md" />,
    title: "Trust",
    description: "Consistent, predictable interactions build user confidence.",
  },
  {
    icon: <Palette className="icon-md" />,
    title: "Craft",
    description: "Attention to detail in spacing, motion, and visual hierarchy.",
  },
];

export default function BrandPage() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Brand Guidelines
            </Text>
            <Badge variant="secondary">Identity</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Our brand is more than a logo. It's the consistent experience users have
            across every touchpoint. These guidelines ensure we present a unified,
            professional identity.
          </Text>
        </div>
      </Section>

      {/* Logo Preview */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Our Identity</CardTitle>
            <CardDescription>The Dream Light brand mark</CardDescription>
          </>
        }
        contentClassName="p-8"
      >
        <div className="flex flex-col items-center gap-6">
          <div className="flex items-center gap-4 p-8 bg-muted/30 rounded-xl">
            <Logo size={64} />
            <div className="flex flex-col">
              <Text as="p" className="text-h3 font-bold">Dream Light</Text>
              <Text as="p" className="text-muted-foreground">Design System</Text>
            </div>
          </div>
          <Text className="text-sm text-muted-foreground text-center max-w-md">
            Our logo represents precision, elegance, and the intricate craft
            that goes into building polished user interfaces.
          </Text>
        </div>
      </SectionCard>
      </Section>

      {/* Brand Values */}
      <Section>
        <div className="space-y-6">
        <Text as="h2" className="text-h4 font-semibold">
          Brand Values
        </Text>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {brandValues.map((value) => (
            <SectionCard key={value.title} contentClassName="p-6">
              <div className="flex flex-col gap-3">
                <div className="text-primary">{value.icon}</div>
                <div className="flex flex-col gap-1">
                  <Text as="p" className="font-semibold">{value.title}</Text>
                  <Text as="p" className="text-sm text-muted-foreground">
                    {value.description}
                  </Text>
                </div>
              </div>
            </SectionCard>
          ))}
        </div>
        </div>
      </Section>

      {/* Brand Sections */}
      <Section>
        <div className="space-y-6">
        <Text as="h2" className="text-h4 font-semibold">
          Brand Assets
        </Text>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {brandSections.map((section) => (
            <Link key={section.href} to={section.href}>
              <SectionCard
                contentClassName="p-6 hover:bg-accent/30 transition-colors cursor-pointer h-full"
              >
                <div className="flex items-start gap-4">
                  <div className="text-primary">{section.icon}</div>
                  <div className="flex flex-col gap-1">
                    <Text as="p" className="font-semibold">{section.title}</Text>
                    <Text as="p" className="text-sm text-muted-foreground">
                      {section.description}
                    </Text>
                  </div>
                </div>
              </SectionCard>
            </Link>
          ))}
        </div>
        </div>
      </Section>

      {/* Usage Principles */}
      <Section>
        <SectionCard
        header={<CardTitle>Brand Usage Principles</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <Text as="p" className="font-semibold text-success">Do</Text>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <div className="text-success">✓</div>
                Use consistent spacing and alignment
              </li>
              <li className="flex gap-2">
                <div className="text-success">✓</div>
                Follow the established color palette
              </li>
              <li className="flex gap-2">
                <div className="text-success">✓</div>
                Maintain proper logo clear space
              </li>
              <li className="flex gap-2">
                <div className="text-success">✓</div>
                Use approved typography only
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-2">
            <Text as="p" className="font-semibold text-destructive">Don&apos;t</Text>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <div className="text-destructive">✗</div>
                Stretch or distort the logo
              </li>
              <li className="flex gap-2">
                <div className="text-destructive">✗</div>
                Use unapproved colors
              </li>
              <li className="flex gap-2">
                <div className="text-destructive">✗</div>
                Add effects or shadows to the logo
              </li>
              <li className="flex gap-2">
                <div className="text-destructive">✗</div>
                Use inconsistent visual treatments
              </li>
            </ul>
          </div>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
